import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeService } from "@/i18n/data.es";
import SunDivider from "./SunDivider";

const SPAN = {
  large: "md:col-span-4 md:row-span-2",
  medium: "md:col-span-2 md:row-span-2",
  small: "md:col-span-2 md:row-span-1",
};

export default function Services() {
  const { t, lang } = useT();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="px-6 md:px-10 lg:px-16 py-24 md:py-32 bg-[var(--surface)]/60 border-y border-[var(--border)] relative grain"
    >
      <div className="text-center mb-12">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          {t("Discover Our Refreshing Care")}
        </div>
        <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[44px] md:text-[64px] leading-[1.02] tracking-tight">
          {t("Helping you reach your")}{" "}
          <span className="italic text-[var(--accent)]">{t("dream")}</span> {t("smile.")}
        </h2>
        <SunDivider className="mt-5" />
        <p className="text-[var(--text-muted)] text-base max-w-xl mx-auto mt-3">
          {t("From a routine cleaning to a full smile redesign — everything done under one warm roof, by doctors who actually pick up the phone.")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[180px] md:auto-rows-[200px] gap-4 md:gap-5">
        {SERVICES.filter((s) => s.featured).map((s, i) => {
          const ls = localizeService(s, lang);
          const big = s.size === "large";
          return (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className={SPAN[s.size]}
            >
              <Link
                data-testid={`service-card-${s.slug}`}
                to={`/services/${s.slug}`}
                className="group relative overflow-hidden rounded-3xl border border-[var(--border)] bg-[var(--bg)] hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] p-6 md:p-7 flex flex-col justify-between h-full"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-14 h-14 rounded-full bg-[var(--primary)] grid place-content-center">
                    <img
                      src={ASSETS.serviceIcons[s.icon]}
                      alt=""
                      className="w-9 h-9 object-contain"
                      aria-hidden="true"
                    />
                  </div>
                  <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:rotate-0 -rotate-12 transition-all" />
                </div>
                <div>
                  <h3
                    className={`font-serif-display ${
                      big
                        ? "text-[36px] md:text-[44px]"
                        : "text-[22px] md:text-[26px]"
                    } leading-[1.05]`}
                  >
                    {ls.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-[var(--text-muted)] group-hover:text-[var(--bg)]/75 max-w-[42ch]">
                    {big ? ls.short : ls.tag}
                  </p>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4 justify-center">
        <Link
          data-testid="see-all-services-btn"
          to="/services"
          className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
        >
          {t("See all services")} <ArrowUpRight className="w-4 h-4" />
        </Link>
        <span className="text-sm text-[var(--text-muted)]">
          {t("Also offering BOTOX® · JUVÉDERM® · Pediatric care")}
        </span>
      </div>
    </section>
  );
}
